/**
 * Active abilities — one per role. Bound to the QWER hotbar in the HUD.
 *
 * Each ability has a cooldown and an "owner role" — the ability fires from
 * the first living party member matching that role. Pressing a key when
 * the owner is dead or on cooldown is a no-op (toast feedback).
 */

import type { Unit } from './units';

export type StatusKind = 'bleed' | 'concussed' | 'slow' | 'rallied' | 'burning';

export interface ActiveStatus {
  kind: StatusKind;
  remaining: number; // seconds
  magnitude: number; // per-tick dmg, or modifier strength
  tickAccum: number;
}

export interface AbilityDef {
  id: string;
  key: 'q' | 'w' | 'e' | 'r' | 't';
  name: string;
  hotkey: string; // user-facing label
  ownerRole: 'vanguard' | 'marksman' | 'medic' | 'mage';
  cooldown: number; // seconds
  shortDesc: string;
}

export const ABILITIES: AbilityDef[] = [
  {
    id: 'cleave',
    key: 'q',
    hotkey: 'Q',
    name: 'Cleave',
    ownerRole: 'vanguard',
    cooldown: 6,
    shortDesc: 'Strike all adjacent enemies for 100% damage.',
  },
  {
    id: 'aimedshot',
    key: 'w',
    hotkey: 'W',
    name: 'Aimed Shot',
    ownerRole: 'marksman',
    cooldown: 8,
    shortDesc: 'Guaranteed crit on the nearest enemy. 2.5× damage.',
  },
  {
    id: 'mend',
    key: 'e',
    hotkey: 'E',
    name: 'Mend',
    ownerRole: 'medic',
    cooldown: 5,
    shortDesc: 'Heal the most-wounded ally for 40 HP.',
  },
  {
    id: 'rally',
    key: 'r',
    hotkey: 'R',
    name: 'Rally',
    ownerRole: 'vanguard',
    cooldown: 30,
    shortDesc: 'Restore +25 morale to every living party member.',
  },
  {
    id: 'firebolt',
    key: 't',
    hotkey: 'T',
    name: 'Firebolt',
    ownerRole: 'mage',
    cooldown: 7,
    shortDesc: 'Launch a fiery bolt at the nearest enemy; sets enemies in a small AoE on fire.',
  },
];

export interface AbilityRuntime {
  def: AbilityDef;
  remainingCooldown: number;
}

export function makeRuntimes(): AbilityRuntime[] {
  return ABILITIES.map(def => ({ def, remainingCooldown: 0 }));
}

export function tickStatuses(unit: Unit, dt: number): { selfDamage: number } {
  if (!unit.statuses) return { selfDamage: 0 };
  let selfDamage = 0;
  for (const st of unit.statuses) {
    st.remaining -= dt;
    st.tickAccum += dt;
    if (st.kind === 'bleed' && st.tickAccum >= 1) {
      st.tickAccum = 0;
      selfDamage += st.magnitude;
    }
    if (st.kind === 'burning' && st.tickAccum >= 0.5) {
      st.tickAccum = 0;
      selfDamage += st.magnitude;
    }
  }
  unit.statuses = unit.statuses.filter(s => s.remaining > 0);
  return { selfDamage };
}

export function hasStatus(unit: Unit, kind: StatusKind): boolean {
  return !!unit.statuses?.some(s => s.kind === kind);
}

export function addStatus(unit: Unit, kind: StatusKind, durationSeconds: number, magnitude: number): void {
  if (!unit.statuses) unit.statuses = [];
  // Refresh if same kind already present; otherwise push.
  const existing = unit.statuses.find(s => s.kind === kind);
  if (existing) {
    existing.remaining = Math.max(existing.remaining, durationSeconds);
    existing.magnitude = Math.max(existing.magnitude, magnitude);
    return;
  }
  unit.statuses.push({ kind, remaining: durationSeconds, magnitude, tickAccum: 0 });
}
